<script lang="ts" setup>
    import { reactive, onMounted } from 'vue';
    import SmsDialogsList from '@shared/components/List/List.vue';
    import { type SmsDialog } from '../TextMessagesTypes';
    import { loadSmsDialogs } from '../TextMessages.access';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import PrePaid from '../Shared/PrePaid.vue';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';

    const state = reactive<{
        smsDialogs: SmsDialog[];
        isBusy: boolean;
    }>({
        smsDialogs: [],
        isBusy: false,
    });

    onMounted(async () => {
        onLoad();
    });

    const onLoad = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        loadSmsDialogs()
            .then((data) => {
                state.smsDialogs = data;
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorTitleLabel, content: $Resources.ErrorLabel });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const filterOptions = () => {
        return ['SmsDialogName', 'VirtualNumber'];
    };
</script>

<script lang="ts"></script>

<template>
    <SmsDialogsList
        :title="$Resources.SmsDialogListHeading"
        :description="$Resources.SmsDialogListText"
        itemId="SmsDialogId"
        :items="state.smsDialogs"
        :loading="state.isBusy"
        :filterKeys="filterOptions()">
        <template #header>
            <PrePaid></PrePaid>
        </template>
        <template #item="{ item, index }">
            <div class="flex-1">
                <h4 class="d-flex flex-wrap gap-xs align-items-baseline">
                    <a :href="'/SmsDialog/Number/' + item.VirtualNumberId">
                        {{ item.SmsDialogName + ' ' + item.VirtualNumber }}
                    </a>
                </h4>

                <ul class="ug-values list-unstyled d-grid gap-sm cols-minmax-120-1fr m-sm-t m-sm-b text-nowrap">
                    <li class="ug-value">
                        <span class="ug-value-label">{{ $Resources.SmsDialogStatsRecipientsLabel }}</span>
                        <span class="ug-value-indicator" :class="item.RecipientCount > 0 && 'success'"></span>
                        {{ formatNumericText(item.RecipientCount, { precision: 1, largeNumbers: true }) }}
                    </li>
                    <li class="ug-value">
                        <span class="ug-value-label">{{ $Resources.SmsDialogStatsRespondentsLabel }}</span>
                        <span class="ug-value-indicator" :class="item.RespondentsCount > 0 && 'success'"></span>
                        {{ formatNumericText(item.RespondentsCount, { precision: 1, largeNumbers: true }) }}
                    </li>
                    <li class="ug-value">
                        <span class="ug-value-label">{{ $Resources.SmsDialogStatsResponsesLabel }}</span>
                        <span class="ug-value-indicator" :class="item.ReplyCount > 0 && 'success'"></span>
                        {{ formatNumericText(item.ReplyCount, { precision: 1, largeNumbers: true }) }}
                    </li>
                    <li class="ug-value">
                        <span class="ug-value-label">{{ $Resources.SmsDialogStatsUnreadLabel }}</span>
                        <span class="ug-value-indicator" :class="item.UnreadCount > 0 && 'success'"></span>
                        {{ formatNumericText(item.UnreadCount, { precision: 1, largeNumbers: true }) }}
                    </li>
                    <li class="ug-value">
                        <span class="ug-value-label">{{ $Resources.SmsDialogStatsUnansweredLabel }}</span>
                        <span class="ug-value-indicator" :class="item.AwaitingActionCount > 0 && 'success'"></span>
                        {{ formatNumericText(item.AwaitingActionCount, { precision: 1, largeNumbers: true }) }}
                    </li>
                </ul>
            </div>
        </template>
    </SmsDialogsList>
</template>

<style></style>
